import type { Stage } from "./types.js";

export interface SettlementPerformanceQuery {
  from: string;
  to: string;
  workerId?: string;
  projectName?: string;
  subtaskName?: string;
  stage?: Stage;
}

export interface SettlementPerformanceDay {
  date: string;
  amount: number;
  quantity: number;
  recordCount: number;
}

export interface SettlementPerformance {
  days: SettlementPerformanceDay[];
  /** Choices remain independent of the selected worker. */
  workers: Array<{ id: string; name: string; amount: number }>;
  firstDate: string | null;
  lastDate: string | null;
  recent: Array<{
    id: string;
    workerId: string;
    workerName: string;
    projectName: string;
    subtaskName: string;
    date: string;
    amount: number;
  }>;
}

/** Preserve the compact card's existing point-endpoint display contract. This
 * endpoint includes all piecework kinds and has no quantity or task metadata. */
export function compactPerformanceFromPoints(
  points: Array<{ at: string; amount: number; workerId: string }>,
  workerNames: ReadonlyMap<string, string>,
  selectedWorkerId: string,
): SettlementPerformance {
  const days = new Map<string, SettlementPerformanceDay>();
  const workers = new Map<string, SettlementPerformance["workers"][number]>();
  const records = points.map((point, index) => ({
    id: `${point.at}-${index}`, workerId: point.workerId,
    workerName: workerNames.get(point.workerId) ?? "이름 없음",
    projectName: "프로젝트", subtaskName: "", date: point.at.slice(0, 10), amount: point.amount,
  }));
  for (const record of records) {
    const worker = workers.get(record.workerId) ?? { id: record.workerId, name: record.workerName, amount: 0 };
    worker.amount += record.amount;
    workers.set(record.workerId, worker);
    if (selectedWorkerId !== "all" && record.workerId !== selectedWorkerId) continue;
    const day = days.get(record.date) ?? { date: record.date, amount: 0, quantity: 0, recordCount: 0 };
    day.amount += record.amount;
    day.recordCount += 1;
    days.set(record.date, day);
  }
  const dates = records.map(record => record.date).sort();
  return {
    days: [...days.values()].sort((left, right) => left.date.localeCompare(right.date)),
    workers: [...workers.values()].sort((left, right) => right.amount - left.amount),
    firstDate: dates[0] ?? null, lastDate: dates.at(-1) ?? null,
    recent: records.filter(record => selectedWorkerId === "all" || record.workerId === selectedWorkerId)
      .sort((left, right) => right.date.localeCompare(left.date)).slice(0, 3),
  };
}
